import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import { useEffect, useMemo, useState } from "react";
import { useReadDatasourceQuery } from "store/api";
import { type DataSourceRecord, resolveEndpoint, useDatasourceOptions } from "./";

type Props = {
  onBack: () => void;
  onSubmit: (data: Record<string, any>) => Promise<void>;
  initialData?: Record<string, any> | null;
};

const CHART_TYPE_OPTIONS = ["Bar", "Pie", "Line", "Doughnut", "Polar", "Radar"];

const ChartWidgetForm: React.FC<Props> = ({ onBack, onSubmit: onSubmitProp, initialData }) => {
  const [title, setTitle] = useState("");
  const [chartType, setChartType] = useState("Bar");
  const [datasource, setDatasource] = useState("");
  const [dataField, setDataField] = useState("");
  const [labelField, setLabelField] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setTitle(String(initialData?.title ?? ""));
    setChartType(String(initialData?.chart ?? "Bar") || "Bar");
    setDatasource(String(initialData?.datasource ?? ""));
    setDataField(String(initialData?.datafield ?? ""));
    setLabelField(String(initialData?.labelField ?? ""));
  }, [initialData]);

  const { dataSources, datasourceOptions } = useDatasourceOptions();

  const previewRequest = useMemo(() => {
    if (!datasource) return null;
    const ds = (Array.isArray(dataSources) ? dataSources : []).find(
      (item: DataSourceRecord) => String(item?.id ?? "") === String(datasource)
    ) as DataSourceRecord | undefined;

    if (!ds?.read?.endpoint) return null;

    return {
      url: resolveEndpoint(ds.read.endpoint),
      method: String(ds?.read?.method ?? "GET"),
      baseUrl: String(ds?.baseUrl ?? ""),
      datasourceId: Number(ds?.id ?? 0),
      layoutId: "widget-ChartWidgetInput",
    };
  }, [datasource, dataSources]);

  const { data: previewData } = useReadDatasourceQuery(previewRequest as any, {
    skip: !previewRequest,
  });

  const previewFields = useMemo(() => {
    const rows = Array.isArray(previewData) ? previewData : [];
    if (!rows.length || typeof rows[0] !== "object" || rows[0] == null) return [] as string[];
    return Object.keys(rows[0] as Record<string, any>);
  }, [previewData]);

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      await onSubmitProp({
        type: "ChartWidget",
        title,
        chart: chartType,
        datasource,
        datafield: dataField,
        labelField,
      });
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type="text"
            label="title"
            name="chart_widget_title"
            value={title}
            onChange={(e: any) => setTitle(String(e.target?.value ?? ""))}
          />
        </group>
      </group>

      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type={InputType.Select}
            label="chart_type"
            name="chart_widget_type"
            value={chartType}
            options={CHART_TYPE_OPTIONS.map((item) => ({ value: item, text: item }))}
            onChange={(v: any) => setChartType(String(v ?? ""))}
          />
        </group>
      </group>

      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type={InputType.Select}
            label="Source"
            name="chart_widget_datasource"
            value={datasource}
            options={datasourceOptions}
            onChange={(v: any) => {
              setDatasource(String(v ?? ""));
              setDataField("");
              setLabelField("");
            }}
          />
        </group>
      </group>

      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type={InputType.Select}
            label="data_field"
            name="chart_widget_datafield"
            value={dataField}
            options={previewFields.map((item) => ({ value: item, text: item }))}
            onChange={(v: any) => setDataField(String(v ?? ""))}
          />
        </group>
      </group>

      <group data-gap="20" data-max-length="500">
        <group data-length="autofit">
          <Input
            dataLength="autofit"
            size="large"
            type={InputType.Select}
            label="label_field"
            name="chart_widget_labelfield"
            value={labelField}
            options={previewFields.map((item) => ({ value: item, text: item }))}
            onChange={(v: any) => setLabelField(String(v ?? ""))}
          />
        </group>
      </group>

      <group data-wrap="no" data-gap="10">
        <Button type="button" primary text="submit" onClick={handleSubmit} disabled={isLoading} />
        <Button type="button" text="back" onClick={onBack} disabled={isLoading} />
      </group>
    </>
  );
};

export default ChartWidgetForm;
